import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import ArticleHeaderImage from '@/assets/agent-user-detail/article-header.webp'
import { Image } from "@/components/common/image";
import { HelpCircleIcon } from "@/components/agent-dashboard-user-detail/assets/icon/help-circle-icon";
import { TextInput } from "@/components/common/text-input";
import { useToast } from "@/hooks/use-toast";
import { Upload } from "@/components/common/upload";
import { cn } from "@/lib/utils";
import { Plus, X } from "lucide-react";
import { LoadingOverlay } from "@/components/common/loading-overlay";
import { ConfirmModal } from "@/components/common/confirm-modal";
import { PreviewVideoImageFull } from "@/components/common/preview-video-image-full";
import ArticlePosterImage from '@/assets/agent-user-detail/article-poster.webp'
import { ArticleSettingData } from "@/manager/manager/agent-dashboard-user-management-manager";
import { useParams } from "react-router-dom";

const ALLOW_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
]

const MAX_FILE_SIZE = 10 * 1024 * 1024;

interface ArticleSettingProps {
  articleHeaderCopy?: string;
  articleDrainageImage?: string;
  uploadArticleSettingImage?: (file: File) => Promise<string>;
  onDataChange?: (changed: boolean) => void;
}

export interface ArticleSettingAction {
  getData: () => ArticleSettingData;
}

/** 文章设置 */
export const ArticleSetting = forwardRef<ArticleSettingAction, ArticleSettingProps>(({
  articleHeaderCopy,
  articleDrainageImage,
  uploadArticleSettingImage,
  onDataChange,
}, ref) => {
  const params = useParams();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [input, setInput] = useState(articleHeaderCopy || '');
  const [imageUrl, setImageUrl] = useState(articleDrainageImage || '');
  const [uploadLoading, setUploadLoading] = useState(false);

  const [hoverHeaderIcon, setHoverHeaderIcon] = useState(false);
  const [hoverImageIcon, setHoverImageIcon] = useState(false);
  const [preview, setPreview] = useState('');

  useEffect(() => {
    setInput(articleHeaderCopy || '');
    setImageUrl(articleDrainageImage || '');
  }, [articleHeaderCopy, articleDrainageImage]);

  // 上传引流底图
  const handleUpload = async (file: File) => {
    try {
      setUploadLoading(true);
      const url = await uploadArticleSettingImage?.(file);
      if (url) setImageUrl(url);
    } catch (error) {
      console.error("Failed to upload article setting image:", error);
    } finally {
      setUploadLoading(false);
    }
  }

  const handleInvalid = () => {
    toast.default({
      title: t("agent_dashboard.user_detail.agent_info.invalid_image"),
      description: t(
        "agent_dashboard.user_detail.agent_info.invalid_image_description"
      ),
    });
  }

  // 删除图片
  const handleDeleteImage = () => {
    setImageUrl('');
    toast.default({
      description: t("agent_dashboard.user_detail.article_bottom_poster.delete_success"),
    });
  }

  useEffect(() => {
    if (uploadLoading) {
      onDataChange?.(false);
      return;
    }
    const isInputChange = input !== articleHeaderCopy;
    const isImageChange = imageUrl !== articleDrainageImage;

    onDataChange?.(isInputChange || isImageChange)
  }, [input, imageUrl, articleHeaderCopy, articleDrainageImage, onDataChange, uploadLoading])

  useImperativeHandle(ref, () => ({
    getData: () => ({
      articleHeaderCopy: input,
      articleDrainageImage: imageUrl,
    }),
  }));

  return (
    <div className="w-full flex flex-col gap-4 relative mb-6">
      {/* 页眉 */}
      <div className="w-full">
        <div className='relative flex items-center gap-1.5 text-base font-medium text-[rgba(0,0,0,1)] mb-5'>
          <span>{t("agent_dashboard.user_detail.article_header.title")}</span>
          <div
            className="max-w-max cursor-pointer"
            onMouseEnter={() => setHoverHeaderIcon(true)}
            onMouseLeave={() => setHoverHeaderIcon(false)}
          >
            <HelpCircleIcon className="w-5 h-5 text-[rgba(102,112,133,1)] hover:text-[rgba(52,64,84,1)]" />
          </div>
          {hoverHeaderIcon && (
            <div className="absolute p-1.5 w-[17.125rem] h-[10.063rem] top-8 -left-0 flex items-center justify-center z-[50] !bg-white bg-[rgba(0,0,0,0.05)] rounded-[12px] [box-shadow:0px_4.6px_9.1px_rgba(0,_0,_0,_0.2)] overflow-hidden" >
              <Image
                src={ArticleHeaderImage}
                alt={t("agent_dashboard.user_detail.article_header.title")}
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
        <div className="w-full ">
          <TextInput
            value={input || ''}
            placeholder={t("agent_dashboard.user_detail.article_header.placeholder")}
            className="h-11"
            onChange={(e) => setInput(e.target.value)}
            maxLength={15}
            showLength
          />
        </div>
      </div>
      {/* 底图 */}
      <div className="w-full">
        <div className="w-full h-full flex flex-col relative">
          <div className='flex items-center gap-1.5 text-base font-medium text-[rgba(0,0,0,1)] mb-5'>
            <span>{t("agent_dashboard.user_detail.article_bottom_poster.title")}</span>
            <div
              className="max-w-max cursor-pointer"
              onMouseEnter={() => setHoverImageIcon(true)}
              onMouseLeave={() => setHoverImageIcon(false)}
            >
              <HelpCircleIcon className="w-5 h-5 text-[rgba(102,112,133,1)] hover:text-[rgba(52,64,84,1)]" />
            </div>
            {hoverImageIcon && (
              <div className="absolute p-1.5 w-[17.125rem] h-[10.063rem] top-8 -left-0 flex items-center justify-center z-[50] !bg-white bg-[rgba(0,0,0,0.05)] rounded-[12px] [box-shadow:0px_4.6px_9.1px_rgba(0,_0,_0,_0.2)] overflow-hidden" >
                <Image
                  src={ArticlePosterImage}
                  alt={t("agent_dashboard.user_detail.article_header.title")}
                  className="w-full h-full object-cover rounded-[8px]"
                />
              </div>
            )}
          </div>
          {!imageUrl && (
            <Upload
              className={cn(
                "relative flex-1 flex flex-col items-center justify-center overflow-hidden rounded-md  w-[18.75rem] max-h-[9.875rem] min-h-[9.875rem] bg-[rgba(249,250,251,1)] transition-colors cursor-pointer",
                "border-2 border-dashed border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)]",
              )}
              acceptedFileTypes={ALLOW_FILE_TYPES.join(',')}
              maxFileSize={MAX_FILE_SIZE}
              minFileSize={0}
              onUpload={handleUpload}
              onInvalid={handleInvalid}
              disabled={uploadLoading}
            >
              <div className='flex flex-col items-center justify-center py-5'>
                <Plus className='size-7 text-[rgba(52,64,84,1)]' />
                <div className='text-center mt-1.6 text-sm leading-5'>
                  <p className='text-[15px] font-semibold text-[rgba(52,64,84,1)]'>
                    {t("agent_dashboard.user_detail.article_bottom_poster.upload")}
                  </p>
                  <p className='mt-[10px] text-xs font-medium text-[rgba(102,112,133,1)]'>
                    1920*1000,JPG、JPEG、PNG,≤ 10MB,≤ 4K
                  </p>
                </div>
              </div>
              <LoadingOverlay loading={uploadLoading} />
            </Upload>
          )}
          {imageUrl && (
            <div className="relative w-[18.75rem] h-[9.875rem] flex items-center justify-center overflow-hidden rounded-[8px]">
              <Image
                src={imageUrl}
                alt={t("agent_dashboard.user_detail.article_header.title")}
                className="max-w-full max-h-full object-contain rounded-[8px]  cursor-pointer"
                onClick={() => setPreview(imageUrl)}
              />
              <ConfirmModal
                trigger={
                  <div
                    className="size-5 rounded-full flex items-center justify-center absolute top-2 right-2 cursor-pointer bg-[rgba(0,0,0,0.4)]"
                  >
                    <X className="size-4 text-white" />
                  </div>
                }
                title={t("agent_dashboard.user_detail.article_bottom_poster.delete_title")}
                description={t("agent_dashboard.user_detail.article_bottom_poster.delete_desc")}
                onConfirm={handleDeleteImage}
                confirm={t("agent_dashboard.user_detail.article_bottom_poster.delete_confirm")}
              />
            </div>
          )}
          <PreviewVideoImageFull
            previewList={[{ url: preview, type: "image" }]}
            open={preview.length > 0}
            onOpenChange={(open) => {
              if (!open) setPreview('');
            }}
          />
        </div>
      </div>
    </div>
  )
})
